import React, { useCallback, useState } from 'react';
import { DeleteConfirmationDialog } from './delete-confirmation-dialog';

export interface ConfirmOptions {
    title?: string;
    content: string;
    /** Confirm button text (default: Delete). */
    confirmLabel?: string;
    /** False for a confirmation that is not a deletion (no "cannot be undone" alert). */
    destructive?: boolean;
    /** Portal target (the extension's shadow-root container); default document.body. */
    container?: Element | null;
}

/**
 * The app's confirm dialog as a promise, a drop-in for `window.confirm` in async handlers:
 * `if (!(await confirm({ content }))) return;` — and render `{dialog}` once in the component.
 */
export function useConfirmDialog(): { confirm: (opts: ConfirmOptions) => Promise<boolean>; dialog: React.ReactNode } {
    const [pending, setPending] = useState<{ opts: ConfirmOptions; resolve: (ok: boolean) => void } | null>(null);
    const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ opts, resolve })), []);
    const settle = (ok: boolean) => {
        pending?.resolve(ok);
        setPending(null);
    };
    const dialog = pending ? (
        <DeleteConfirmationDialog
            open
            onClose={() => settle(false)}
            onConfirm={() => settle(true)}
            title={pending.opts.title}
            content={pending.opts.content}
            confirmLabel={pending.opts.confirmLabel}
            showWarning={pending.opts.destructive !== false}
            container={pending.opts.container}
        />
    ) : null;
    return { confirm, dialog };
}
